import React, { useState, useCallback } from "react";
import Swal from "sweetalert2";
import DashboardWrapper from "../../components/DashboardWrapper";
import authService from "../../services/authService";
import Breadcrumb from "../../components/Breadcrumbdashboard";

export default function ChangePassword() {
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // ============================================================
  // SWEET ALERT
  // ============================================================

  const showSwal = useCallback((title, text, icon) => {
    Swal.fire({
      title,
      text,
      icon,
      confirmButtonText: "OK",
      customClass: {
        confirmButton: "btn btn-primary",
      },
      buttonsStyling: false,
    });
  }, []);

  // ============================================================
  // INPUT CHANGE
  // ============================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ============================================================
  // SHOW / HIDE PASSWORD
  // ============================================================

  const togglePassword = (field) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // ============================================================
  // SUBMIT
  // ============================================================

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ----------------------------------------------------------
    // CHECK NEW PASSWORD LENGTH
    // ----------------------------------------------------------

    if (formData.new_password.length < 6) {
      showSwal(
        "Validation",
        "New password must be at least 6 characters.",
        "warning"
      );
      return;
    }

    // ----------------------------------------------------------
    // CHECK PASSWORD MATCH
    // ----------------------------------------------------------

    if (
      formData.new_password !==
      formData.confirm_password
    ) {
      showSwal(
        "Validation",
        "Passwords do not match.",
        "warning"
      );
      return;
    }

    // ----------------------------------------------------------
    // CHECK CURRENT AND NEW PASSWORD
    // ----------------------------------------------------------

    if (
      formData.current_password ===
      formData.new_password
    ) {
      showSwal(
        "Validation",
        "New password must be different from current password.",
        "warning"
      );
      return;
    }

    // ----------------------------------------------------------
    // PAYLOAD
    // ----------------------------------------------------------

    const payload = {
      current_password:
        formData.current_password,

      new_password:
        formData.new_password,

      new_password_confirmation:
        formData.confirm_password,
    };

    console.log(
      "Change Password Payload:",
      payload
    );

    setLoading(true);

    try {
      // --------------------------------------------------------
      // API CALL
      // --------------------------------------------------------

      const response =
        await authService.changePassword(
          payload
        );

      console.log(
        "Change Password Response:",
        response?.data
      );

      // --------------------------------------------------------
      // SUCCESS
      // --------------------------------------------------------

      showSwal(
        "Success",
        response?.data?.message ||
          "Password changed successfully.",
        "success"
      );

      // --------------------------------------------------------
      // CLEAR FORM
      // --------------------------------------------------------

      setFormData({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });

      setShowPassword({
        current: false,
        new: false,
        confirm: false,
      });
    } catch (error) {
      console.error(
        "Change Password Error:",
        error?.response?.data || error
      );

      // --------------------------------------------------------
      // VALIDATION ERRORS
      // --------------------------------------------------------

      const validationErrors =
        error?.response?.data?.errors;

      let errorMessage =
        error?.response?.data?.message ||
        "Failed to change password.";

      if (validationErrors) {
        const firstError =
          Object.values(validationErrors)
            .flat()
            .find(Boolean);

        if (firstError) {
          errorMessage = firstError;
        }
      }

      showSwal(
        "Error",
        errorMessage,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // UI
  // ============================================================

  return (
    <DashboardWrapper>
      <div className="container-fluid mt-4">

        {/* ======================================================
            BREADCRUMB
        ====================================================== */}

        <div className="d-flex justify-content-between align-items-center mb-4">
          <Breadcrumb />
        </div>

        {/* ======================================================
            CARD
        ====================================================== */}

        <div className="card shadow col-8">

          <div className="card-header">
            {/* Change Password */}
          </div>

          <div className="card-body">

            <form onSubmit={handleSubmit}>

              <div className="row">

                {/* ==================================================
                    CURRENT PASSWORD
                ================================================== */}

                <div className="col-12 mb-3">

                  <label className="form-label">
                    Current Password
                  </label>

                  <div className="input-group">

                    <input
                      type={
                        showPassword.current
                          ? "text"
                          : "password"
                      }
                      name="current_password"
                      className="form-control"
                      value={
                        formData.current_password
                      }
                      onChange={handleChange}
                      placeholder="Enter current password"
                      autoComplete="current-password"
                      required
                    />

                    <span
                      className="input-group-text cursor-pointer"
                      onClick={() =>
                        togglePassword(
                          "current"
                        )
                      }
                    >
                      <i
                        className={`bx ${
                          showPassword.current
                            ? "bx-show"
                            : "bx-hide"
                        }`}
                      />
                    </span>

                  </div>

                </div>

                {/* ==================================================
                    NEW PASSWORD
                ================================================== */}

                <div className="col-12 mb-3">

                  <label className="form-label">
                    New Password
                  </label>

                  <div className="input-group">

                    <input
                      type={
                        showPassword.new
                          ? "text"
                          : "password"
                      }
                      name="new_password"
                      className="form-control"
                      value={
                        formData.new_password
                      }
                      onChange={handleChange}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      minLength={6}
                      required
                    />

                    <span
                      className="input-group-text cursor-pointer"
                      onClick={() =>
                        togglePassword(
                          "new"
                        )
                      }
                    >
                      <i
                        className={`bx ${
                          showPassword.new
                            ? "bx-show"
                            : "bx-hide"
                        }`}
                      />
                    </span>

                  </div>

                </div>

                {/* ==================================================
                    CONFIRM PASSWORD
                ================================================== */}

                <div className="col-12 mb-3">

                  <label className="form-label">
                    Confirm New Password
                  </label>

                  <div className="input-group">

                    <input
                      type={
                        showPassword.confirm
                          ? "text"
                          : "password"
                      }
                      name="confirm_password"
                      className="form-control"
                      value={
                        formData.confirm_password
                      }
                      onChange={handleChange}
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                      minLength={6}
                      required
                    />

                    <span
                      className="input-group-text cursor-pointer"
                      onClick={() =>
                        togglePassword(
                          "confirm"
                        )
                      }
                    >
                      <i
                        className={`bx ${
                          showPassword.confirm
                            ? "bx-show"
                            : "bx-hide"
                        }`}
                      />
                    </span>

                  </div>

                </div>

              </div>

              {/* ==================================================
                  BUTTON
              ================================================== */}

              <div className="mt-3">

                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Changing...
                    </>
                  ) : (
                    "Change Password"
                  )}
                </button>

              </div>

            </form>

          </div>

        </div>

      </div>
    </DashboardWrapper>
  );
}